import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Card, Col, Row, Statistic, Tooltip, Typography } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { getUsageMetrics, MetricsPeriod, ProcessingTime } from '../../../Actions/operationsMetricsApi'
import { formatNumber, formatSeconds } from './formatters'
import { StatusTags } from './statusMeta'

function ProcessingTimeLine({ time, approximate }: { time: ProcessingTime | null; approximate?: boolean }) {
  if (!time) return null
  return (
    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
      처리시간 평균 {formatSeconds(time.avgSeconds)} · p50 {formatSeconds(time.p50Seconds)} · p90{' '}
      {formatSeconds(time.p90Seconds)}
      {approximate && (
        <Tooltip title="전용 완료시각 컬럼이 없어 created_at→updated_at 근사치입니다.">
          {' '}
          <InfoCircleOutlined />
        </Tooltip>
      )}
    </Typography.Text>
  )
}

interface Props {
  period: MetricsPeriod
}

/** 🎬 서비스 사용량 — Vgen/Aivatar/AI이미지/News 생성수·상태 분포·처리시간 */
export default function UsageSection({ period }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['operationsMetrics', 'usage', period],
    queryFn: () => getUsageMetrics(period),
  })

  if (error) return <Alert type="error" message="사용량 지표를 불러오지 못했습니다." description={(error as Error).message} />

  return (
    <Card title="🎬 서비스 사용량" size="small">
      <Row gutter={[12, 12]}>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small" loading={isLoading}>
            <Statistic title="Vgen 비디오 생성" value={data ? formatNumber(data.vgenVideo.total) : '—'} />
            <div style={{ marginTop: 8 }}>
              <StatusTags statusCounts={data?.vgenVideo.statusCounts ?? {}} />
            </div>
            <ProcessingTimeLine
              time={data?.vgenVideo.processingTime ?? null}
              approximate={data?.vgenVideo.processingTimeApproximate}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small" loading={isLoading}>
            <Statistic title="Aivatar 비디오 생성" value={data ? formatNumber(data.aivatarVideo.total) : '—'} />
            <div style={{ marginTop: 8 }}>
              <StatusTags statusCounts={data?.aivatarVideo.statusCounts ?? {}} />
            </div>
            <ProcessingTimeLine
              time={data?.aivatarVideo.processingTime ?? null}
              approximate={data?.aivatarVideo.processingTimeApproximate}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small" loading={isLoading}>
            <Statistic title="AI 이미지 생성 요청" value={data ? formatNumber(data.aiImage.total) : '—'} />
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              요청 {data ? formatNumber(data.aiImage.requestedImages) : '—'}장 / 완성{' '}
              {data ? formatNumber(data.aiImage.completedImages) : '—'}장
            </Typography.Text>
            <div style={{ marginTop: 8 }}>
              <StatusTags statusCounts={data?.aiImage.statusCounts ?? {}} />
            </div>
            <ProcessingTimeLine time={data?.aiImage.processingTime ?? null} />
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small" loading={isLoading}>
            <Statistic title="News 영상 생성 (완료)" value={data ? formatNumber(data.news.total) : '—'} />
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              소비 크레딧 {data ? formatNumber(data.news.consumedCredits) : '—'}
            </Typography.Text>
          </Card>
        </Col>
      </Row>
    </Card>
  )
}
